// Authentication Service
// Note: persistence is configured by each app when initializing Firebase
// (browserLocalPersistence on web, AsyncStorage-backed persistence on mobile).
// Core avoids web-only APIs so it can run unchanged on React Native.
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { getMaintenanceSettings } from './admin';
import { notifyAdminsOnly, notifyModeratorsOnly, NOTIFICATION_TYPES } from './notifications';
import { stripHtml, validatePhone } from '../utils/validation';
import logger from '../utils/logger';

/**
 * Kullanıcı girişi yapar.
 * Oturum kalıcılığı app init'te sabitlenir; rememberMe parametresi geri uyum
 * için imzada tutuldu, core içinde kullanılmıyor.
 */
export const loginUser = async (email, password, _rememberMe = true) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { success: true, user: userCredential.user };
  } catch (error) {
    return { success: false, error: getErrorMessage(error.code) };
  }
};

/**
 * Google ile giriş sonrası ortak: user-doc yoksa oluşturur, bakım modunu kontrol eder.
 * Web (popup) ve mobile (credential) akışları bu helper'ı kullanır.
 */
const finalizeGoogleSignIn = async (user) => {
  const userDoc = await getDoc(doc(db, 'users', user.uid));
  if (!userDoc.exists()) {
    const maintenance = await getMaintenanceSettings();
    if (maintenance.success && maintenance.data?.enabled) {
      await signOut(auth);
      return { success: false, error: 'Site şu an bakımda. Kayıt ve giriş geçici olarak kapalı.' };
    }
    const nameParts = user.displayName?.split(' ') || ['', ''];
    const now = new Date().toISOString();
    await setDoc(doc(db, 'users', user.uid), {
      email: user.email,
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
      dateOfBirth: '',
      phone: '',
      photoURL: user.photoURL || '',
      createdAt: now,
      roles: ['user'],
      authProvider: 'google',
      profileCompleted: false,
      termsAcceptedAt: null,
      kvkkAcceptedAt: null,
      sessionTimeout: 30,
      accountStatus: 'active',
      isOnline: true
    });
  }
  return { success: true, user };
};

/**
 * Google ile giriş yapar (web — popup).
 */
export const loginWithGoogle = async (_rememberMe = true) => {
  try {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const userCredential = await signInWithPopup(auth, provider);
    return await finalizeGoogleSignIn(userCredential.user);
  } catch (error) {
    return { success: false, error: getErrorMessage(error.code) };
  }
};

/**
 * Google ile giriş yapar (mobile — ID token tabanlı).
 * Mobile'da expo-auth-session ile alınan idToken bu fonksiyona geçilir.
 * @param {string} idToken - Google'dan dönen ID token
 * @param {string} [accessToken] - Opsiyonel access token
 */
export const loginWithGoogleCredential = async (idToken, accessToken = null) => {
  try {
    if (!idToken) {
      return { success: false, error: 'Google kimlik doğrulaması alınamadı.' };
    }
    const credential = GoogleAuthProvider.credential(idToken, accessToken);
    const userCredential = await signInWithCredential(auth, credential);
    return await finalizeGoogleSignIn(userCredential.user);
  } catch (error) {
    return { success: false, error: getErrorMessage(error.code) };
  }
};

/**
 * Telefon numarasının başka bir hesapta kullanılıp kullanılmadığını kontrol eder.
 * @param {string} phone - Kontrol edilecek telefon numarası
 * @param {string} [excludeUserId] - Bu kullanıcı hariç tutulur (profil güncelleme için)
 * @returns {Promise<{available: boolean}>}
 */
export const checkPhoneAvailability = async (phone, excludeUserId = null) => {
  try {
    if (!phone || phone.trim().length < 10) return { available: true };
    const normalizedPhone = phone.replace(/\s+/g, '').replace(/[^+\d]/g, '');
    const q = query(collection(db, 'users'), where('phone', '==', normalizedPhone));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return { available: true };
    // Kendi hesabı hariç tut
    if (excludeUserId) {
      const otherUsers = snapshot.docs.filter(d => d.id !== excludeUserId);
      return { available: otherUsers.length === 0 };
    }
    return { available: false };
  } catch (error) {
    logger.error('Telefon kontrolü hatası:', error);
    return { available: true }; // Hata durumunda engelleme
  }
};

/**
 * Yeni kullanıcı kaydı yapar
 * @param {string} email - Kullanıcı e-posta adresi
 * @param {string} password - Kullanıcı parolası
 * @param {Object} userData - Kullanıcı ek bilgileri (ad, soyad, yaş, telefon)
 * @returns {Promise} Firebase user credential
 */
export const registerUser = async (email, password, userData) => {
  try {
    const maintenance = await getMaintenanceSettings();
    if (maintenance.success && maintenance.data?.enabled) {
      return { success: false, error: 'Site şu an bakımda. Kayıt geçici olarak kapalı.' };
    }
    logger.log('📧 Kayıt verileri:', {
      email,
      passwordLength: password?.length,
      userData
    });

    // Telefon numarası benzersizlik kontrolü
    if (userData.phone) {
      const phoneCheck = await checkPhoneAvailability(userData.phone);
      if (!phoneCheck.available) {
        return { success: false, error: 'Bu telefon numarası başka bir hesapta kullanılıyor.' };
      }
    }

    // 1. Firebase Auth ile kullanıcı oluştur
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    logger.log('✅ Firebase Auth kullanıcısı oluşturuldu:', user.uid);

    // 2. Kullanıcı profil bilgilerini güncelle
    await updateProfile(user, {
      displayName: `${userData.firstName} ${userData.lastName}`
    });
    logger.log('✅ Kullanıcı profili güncellendi');

    // 3. Firestore'da kullanıcı dökümanı oluştur (Kullanım Koşulları ve KVKK onayı kayıtta alınır)
    const now = new Date().toISOString();
    await setDoc(doc(db, 'users', user.uid), {
      email: email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      dateOfBirth: userData.dateOfBirth || '',
      phone: userData.phone ? userData.phone.replace(/\s+/g, '').replace(/[^+\d]/g, '') : '',
      createdAt: now,
      roles: ['user'], // varsayılan rol (array)
      authProvider: 'email',
      termsAcceptedAt: userData.termsAcceptedAt || now,
      kvkkAcceptedAt: userData.kvkkAcceptedAt || now
    });
    logger.log('✅ Firestore kullanıcı dökümanı oluşturuldu');

    // E-posta doğrulama linki gönder
    try {
      await sendEmailVerification(user);
      logger.log('✅ E-posta doğrulama linki gönderildi');
    } catch (verifyErr) {
      logger.warn('E-posta doğrulama gönderilemedi:', verifyErr.message);
    }

    return { success: true, user: user };
  } catch (error) {
    logger.error('❌ Kayıt hatası detayı:');
    logger.error('Hata kodu:', error.code);
    logger.error('Hata mesajı:', error.message);
    logger.error('Tam hata:', error);
    return { success: false, error: getErrorMessage(error.code) };
  }
};

/**
 * Kullanıcı çıkışı yapar.
 * Önce Firestore'da isOnline: false yapılır (oturum açıkken), sonra signOut.
 */
export const logoutUser = async () => {
  try {
    const uid = auth.currentUser?.uid;
    if (uid) {
      try {
        const userRef = doc(db, 'users', uid);
        await updateDoc(userRef, { isOnline: false });
      } catch (e) {
        // Firestore güncellemesi başarısız olsa da çıkış yap
      }
    }
    await signOut(auth);
    return { success: true };
  } catch (error) {
    const message = getErrorMessage(error?.code) || error?.message || 'Çıkış yapılırken bir hata oluştu.';
    return { success: false, error: message };
  }
};

/**
 * Şifre sıfırlama e-postası gönderir
 * @param {string} email - Kullanıcı e-posta adresi
 * @returns {Promise}
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    return { success: false, error: getErrorMessage(error.code) };
  }
};

/**
 * E-posta doğrulama linkini tekrar gönderir
 * @returns {Promise}
 */
export const resendEmailVerification = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return { success: false, error: 'Oturum açık değil.' };
    if (user.emailVerified) return { success: false, error: 'E-posta zaten doğrulanmış.' };
    await sendEmailVerification(user);
    return { success: true };
  } catch (error) {
    if (error.code === 'auth/too-many-requests') {
      return { success: false, error: 'Çok fazla deneme. Lütfen birkaç dakika bekleyin.' };
    }
    return { success: false, error: getErrorMessage(error.code) };
  }
};

/**
 * Oturum açmış kullanıcının şifresini değiştirir (eski şifre ile doğrulama gerektirir)
 * @param {string} currentPassword - Mevcut şifre
 * @param {string} newPassword - Yeni şifre
 * @returns {Promise}
 */
export const changePassword = async (currentPassword, newPassword) => {
  try {
    const user = auth.currentUser;
    if (!user) return { success: false, error: 'Oturum açık değil.' };
    if (!user.email) return { success: false, error: 'E-posta adresi bulunamadı.' };

    // Önce mevcut şifreyle yeniden doğrula
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);

    // Yeni şifreyi ayarla
    await updatePassword(user, newPassword);
    return { success: true };
  } catch (error) {
    if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
      return { success: false, error: 'Mevcut şifreniz hatalı.' };
    }
    if (error.code === 'auth/weak-password') {
      return { success: false, error: 'Yeni şifre çok zayıf. En az 6 karakter olmalıdır.' };
    }
    return { success: false, error: getErrorMessage(error.code) };
  }
};

/**
 * Kullanıcının Kullanım Koşulları ve KVKK onayını günceller (profil sayfasında geç onay için)
 * @param {string} userId - Kullanıcı ID
 * @param {Object} consent - { termsAcceptedAt?, kvkkAcceptedAt? } ISO tarih string
 * @returns {Promise}
 */
export const updateUserLegalConsent = async (userId, consent) => {
  try {
    const userRef = doc(db, 'users', userId);
    const updates = {};
    if (consent.termsAcceptedAt) updates.termsAcceptedAt = consent.termsAcceptedAt;
    if (consent.kvkkAcceptedAt) updates.kvkkAcceptedAt = consent.kvkkAcceptedAt;
    if (Object.keys(updates).length === 0) return { success: false, error: 'Onay verisi gerekli' };
    await updateDoc(userRef, updates);
    return { success: true };
  } catch (error) {
    return { success: false, error: error?.message || 'Onay güncellenemedi.' };
  }
};

/**
 * Firestore'dan kullanıcı bilgilerini getirir
 * @param {string} userId - Kullanıcı ID
 * @returns {Promise}
 */
export const getUserData = async (userId) => {
  try {
    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: false, error: 'Kullanıcı bulunamadı' };
    }
  } catch (error) {
    return { success: false, error: getErrorMessage(error.code) };
  }
};

/**
 * Kullanıcıyı seller yapar (seller rolü ekler ve sellerProfiles collection'ına kaydeder)
 * @param {string} userId - Kullanıcı ID
 * @param {Object} sellerData - Satıcı bilgileri (publicName, sellerType, location, printerInfo, designerInfo)
 * @returns {Promise}
 */
export const becomeSeller = async (userId, sellerData) => {
  try {
    const maintenance = await getMaintenanceSettings();
    if (maintenance.success && maintenance.data?.enabled) {
      return { success: false, error: 'Site şu an bakımda. Satıcı başvurusu geçici olarak kapalı.' };
    }
    logger.log('🔍 becomeSeller servisi başladı. UserID:', userId);
    logger.log('📝 Gelen sellerData:', sellerData);

    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      logger.error('❌ Kullanıcı bulunamadı!');
      return { success: false, error: 'Kullanıcı bulunamadı' };
    }

    logger.log('✅ Kullanıcı bulundu:', userDoc.data());

    // Daha önce başvuru yapıp yapmadığını kontrol et (sellerProfiles'da kayıt var mı?)
    const sellerProfileRef = doc(db, 'sellerProfiles', userId);
    const existingProfile = await getDoc(sellerProfileRef);
    
    if (existingProfile.exists()) {
      const profileData = existingProfile.data();
      if (profileData.approvalStatus === 'pending') {
        logger.warn('⚠️ Kullanıcının bekleyen başvurusu var!');
        return { 
          success: false, 
          error: '⏳ Zaten bekleyen bir başvurunuz var. Her kullanıcı sadece bir dükkan açabilir. Admin onayını bekleyin.' 
        };
      } else if (profileData.approvalStatus === 'approved') {
        logger.warn('⚠️ Kullanıcı zaten onaylanmış seller!');
        return { 
          success: false, 
          error: '✅ Zaten aktif bir satıcı hesabınız var. Her kullanıcı sadece bir dükkan sahibi olabilir. Profilinize gidin.' 
        };
      }
      // rejected ise yeniden başvurabilir, devam et
      logger.log('ℹ️ Önceki başvuru reddedilmiş, yeni başvuru yapılabilir');
    }

    // Mesafeli Satış / Satıcı sözleşmesi onayı zorunlu
    if (!sellerData.sellerTermsAcceptedAt) {
      logger.error('❌ Mesafeli Satış Sözleşmesi ve Satıcı Sözleşmesi onayı gerekli');
      return { success: false, error: 'Mesafeli Satış Sözleşmesi ve Satıcı Sözleşmesini kabul etmeniz gerekmektedir.' };
    }

    // Location validation
    if (!sellerData.location || !sellerData.location.city) {
      logger.error('❌ Location bilgisi eksik!');
      return { success: false, error: 'Lokasyon bilgisi eksik' };
    }

    // Vergi tipi validation (TAX_EXEMPT = vergiden muaf, COMPANY = sirket/firma)
    const VALID_TAX_TYPES = ['TAX_EXEMPT', 'COMPANY'];
    if (!sellerData.taxType || !VALID_TAX_TYPES.includes(sellerData.taxType)) {
      logger.error('❌ Geçersiz veya eksik vergi tipi:', sellerData.taxType);
      return { success: false, error: 'Vergi durumunuzu seçmeniz gerekmektedir (Vergiden Muaf veya Şirket/Firma).' };
    }
    if (!sellerData.taxDocumentUrl) {
      logger.error('❌ Vergi belgesi yüklenmedi');
      return {
        success: false,
        error: sellerData.taxType === 'COMPANY'
          ? 'Vergi levhanızı yüklemeniz gerekmektedir.'
          : 'Vergi muafiyet yazınızı yüklemeniz gerekmektedir.'
      };
    }
    if (sellerData.taxType === 'COMPANY') {
      if (!sellerData.companyName || !sellerData.companyName.trim()) {
        return { success: false, error: 'Firma adı zorunludur.' };
      }
      if (!sellerData.taxNumber || !/^\d{10,11}$/.test(String(sellerData.taxNumber).trim())) {
        return { success: false, error: 'Geçerli bir vergi numarası giriniz (10-11 hane).' };
      }
    }

    // Satıcı türü validation (sunucu tarafı — doğrudan API çağrısı bypass'ını engeller)
    const VALID_SELLER_TYPES = ['PRINTER', 'DESIGNER', 'BOTH'];
    if (!sellerData.sellerType || !VALID_SELLER_TYPES.includes(sellerData.sellerType)) {
      return { success: false, error: 'Geçerli bir satıcı türü seçmeniz gerekmektedir.' };
    }

    // Mağaza adı validation (3-100 karakter)
    const publicNameTrimmed = typeof sellerData.publicName === 'string' ? sellerData.publicName.trim() : '';
    if (publicNameTrimmed.length < 3 || publicNameTrimmed.length > 100) {
      return { success: false, error: 'Mağaza adı 3-100 karakter arasında olmalıdır.' };
    }

    // Telefon validation (verildiyse geçerli Türk telefon formatı olmalı)
    if (sellerData.phone && !validatePhone(sellerData.phone)) {
      return { success: false, error: 'Geçerli bir telefon numarası giriniz.' };
    }

    // Yazıcı bilgisi validation (PRINTER/BOTH)
    if (sellerData.sellerType === 'PRINTER' || sellerData.sellerType === 'BOTH') {
      const brand = sellerData.printerInfo && sellerData.printerInfo.brandAndModel;
      if (!brand || !String(brand).trim() || String(brand).length > 100) {
        return { success: false, error: 'Yazıcı marka/modeli 1-100 karakter olmalıdır.' };
      }
    }

    // Tasarımcı bilgisi validation (DESIGNER/BOTH)
    if (sellerData.sellerType === 'DESIGNER' || sellerData.sellerType === 'BOTH') {
      const di = sellerData.designerInfo || {};
      if (!Array.isArray(di.softwareUsed) || di.softwareUsed.length === 0) {
        return { success: false, error: 'Tasarımcı satıcılar en az bir yazılım seçmelidir.' };
      }
      if (!Array.isArray(di.specialties) || di.specialties.length === 0) {
        return { success: false, error: 'Tasarımcı satıcılar en az bir uzmanlık alanı seçmelidir.' };
      }
    }

    // Seller profil verisi hazırla
    const sellerProfile = {
      userId: userId,
      publicName: stripHtml(publicNameTrimmed),
      sellerType: sellerData.sellerType,
      phone: sellerData.phone || '', // Satıcı başvuru formundan gelen telefon
      location: sellerData.location,
      avgRating: sellerData.avgRating || 0,
      ratingCount: sellerData.ratingCount || 0,
      totalOrders: 0,
      completedOrders: 0,
      isActive: false, // ⏳ Admin onayı bekliyor (önceden true idi)
      approvalStatus: 'pending', // pending, approved, rejected
      approvalDate: null,
      approvedBy: null,
      rejectionReason: null,
      sellerTermsAcceptedAt: sellerData.sellerTermsAcceptedAt, // Mesafeli Satış / Satıcı sözleşmesi onay tarihi
      taxType: sellerData.taxType,
      taxDocumentUrl: sellerData.taxDocumentUrl,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    if (sellerData.taxType === 'COMPANY') {
      sellerProfile.companyName = String(sellerData.companyName).trim();
      sellerProfile.taxNumber = String(sellerData.taxNumber).trim();
    }

    // Profil görseli varsa ekle
    if (sellerData.profileImageUrl) {
      logger.log('🖼️ Profil görseli ekleniyor:', sellerData.profileImageUrl);
      sellerProfile.profileImageUrl = sellerData.profileImageUrl;
    }

    // Printer bilgilerini ekle (eğer PRINTER veya BOTH ise)
    if (sellerData.printerInfo && (sellerData.sellerType === 'PRINTER' || sellerData.sellerType === 'BOTH')) {
      logger.log('🖨️ Printer bilgileri ekleniyor...');
      sellerProfile.printerInfo = sellerData.printerInfo;
    }

    // Designer bilgilerini ekle (eğer DESIGNER veya BOTH ise)
    if (sellerData.designerInfo && (sellerData.sellerType === 'DESIGNER' || sellerData.sellerType === 'BOTH')) {
      logger.log('🎨 Designer bilgileri ekleniyor...');
      sellerProfile.designerInfo = sellerData.designerInfo;
    }

    logger.log('📦 Hazırlanan sellerProfile:', sellerProfile);

    // ⚠️ NOT: Seller rolü EKLENMIYOR! Admin onayladığında eklenecek.
    // Sadece sellerProfiles collection'ına başvuru kaydediliyor.
    
    logger.log('💾 Seller başvurusu kaydediliyor...');
    await setDoc(sellerProfileRef, sellerProfile);
    logger.log('✅ Seller başvurusu kaydedildi - Admin onayı bekleniyor');

    // Sadece Admin'lere bildirim gönder
    await notifyAdminsOnly(
      NOTIFICATION_TYPES.NEW_SELLER_APPLICATION,
      'Yeni Satıcı Başvurusu',
      `${sellerData.publicName} adlı kullanıcı satıcı başvurusu yaptı`,
      {
        link: `/admin?tab=pending-approvals`,
        userId: userId,
        sellerName: sellerData.publicName
      }
    );

    return { success: true };
  } catch (error) {
    logger.error('❌ Become seller error:', error);
    logger.error('Error code:', error.code);
    logger.error('Error message:', error.message);
    logger.error('Error stack:', error.stack);
    return { success: false, error: `Hata: ${error.message || 'Satıcı olma işlemi başarısız oldu.'}` };
  }
};

/**
 * Satıcı profilini getirir
 * @param {string} userId - Kullanıcı ID
 * @returns {Promise}
 */
export const getSellerProfile = async (userId) => {
  try {
    const sellerRef = doc(db, 'sellerProfiles', userId);
    const sellerSnap = await getDoc(sellerRef);
    
    if (sellerSnap.exists()) {
      return { success: true, data: sellerSnap.data() };
    } else {
      return { success: false, error: 'Satıcı profili bulunamadı' };
    }
  } catch (error) {
    logger.error('Get seller profile error:', error);
    return { success: false, error: 'Satıcı profili alınamadı.' };
  }
};

/**
 * Satıcı profilini günceller
 * @param {string} userId - Kullanıcı ID
 * @param {Object} updateData - Güncellenecek veriler
 * @returns {Promise}
 */
export const updateSellerProfile = async (userId, updateData) => {
  try {
    const sellerRef = doc(db, 'sellerProfiles', userId);
    const sellerSnap = await getDoc(sellerRef);
    
    if (!sellerSnap.exists()) {
      return { success: false, error: 'Satıcı profili bulunamadı' };
    }
    
    await updateDoc(sellerRef, {
      ...updateData,
      updatedAt: new Date().toISOString()
    });

    if (updateData.publicName != null) {
      const existing = sellerSnap.data() || {};
      const oldName = existing.publicName || '';
      if (oldName !== updateData.publicName) {
        await notifyModeratorsOnly(
          NOTIFICATION_TYPES.SELLER_NAME_CHANGE_REQUEST,
          'Satıcı isim değişikliği talebi',
          `Satıcı mağaza adını "${oldName}" → "${updateData.publicName}" olarak değiştirdi`,
          { link: '/admin?tab=sellers', userId, sellerName: updateData.publicName }
        );
      }
    }

    return { success: true, message: 'Satıcı profili başarıyla güncellendi!' };
  } catch (error) {
    logger.error('Update seller profile error:', error);
    return { success: false, error: 'Satıcı profili güncellenirken bir hata oluştu.' };
  }
};

/**
 * Kullanıcının belirli bir rolü var mı kontrol eder
 * @param {Array} userRoles - Kullanıcının rolleri
 * @param {string} role - Kontrol edilecek rol
 * @returns {boolean}
 */
export const hasRole = (userRoles, role) => {
  return userRoles && Array.isArray(userRoles) && userRoles.includes(role);
};

/**
 * Firebase hata kodlarını Türkçe mesajlara çevirir
 * @param {string} errorCode - Firebase hata kodu
 * @returns {string} Türkçe hata mesajı
 */
const getErrorMessage = (errorCode) => {
  const errorMessages = {
    'auth/email-already-in-use': 'Bu e-posta adresi zaten kullanımda.',
    'auth/invalid-email': 'Geçersiz e-posta adresi.',
    'auth/operation-not-allowed': 'İşlem izni yok.',
    'auth/weak-password': 'Parola çok zayıf. En az 6 karakter olmalıdır.',
    'auth/password-does-not-meet-requirements': '⚠️ Parola gereksinimleri: En az 1 büyük harf, 1 rakam ve 1 özel karakter (!@#$%^&*) içermelidir.',
    'auth/user-disabled': 'Bu kullanıcı hesabı devre dışı bırakıldı.',
    'auth/user-not-found': 'Kullanıcı bulunamadı.',
    'auth/wrong-password': 'Hatalı parola.',
    'auth/invalid-credential': 'Geçersiz kimlik bilgileri.',
    'auth/too-many-requests': 'Çok fazla deneme. Lütfen daha sonra tekrar deneyin.',
    'auth/network-request-failed': 'Ağ bağlantısı hatası. İnternet bağlantınızı kontrol edin.',
    'auth/popup-closed-by-user': 'Giriş işlemi iptal edildi.',
    'auth/cancelled-popup-request': 'Giriş işlemi iptal edildi.',
    'auth/popup-blocked': 'Popup engellendi. Lütfen tarayıcı ayarlarınızı kontrol edin.',
    'auth/account-exists-with-different-credential': 'Bu e-posta adresi farklı bir giriş yöntemiyle kayıtlı.'
  };

  return errorMessages[errorCode] || 'Bir hata oluştu. Lütfen tekrar deneyin.';
};

